/**
 * Shared types + helpers for fields that may be plain strings (legacy) or
 * localized objects ({ ru, en, kk }). Used by in-app broadcasts to ship a
 * single payload that renders on the user's current language.
 *
 * Storage convention:
 *   - Plain string  → legacy single-language content (assumed Russian).
 *   - Object        → { ru: '...', en?: '...', kk?: '...' } stored as-is in
 *                     JSON columns (notifications.actions, .metadata) or
 *                     JSON-encoded inside string columns (notifications.title,
 *                     .message). The backend stringifies the localized object
 *                     into title/message for backwards-compat with old clients
 *                     that don't know about localization.
 *
 * Read path:
 *   pickLocalized(value, lang) → string
 *     - Returns value as-is if it's a string.
 *     - Returns value[lang] with fallback chain ru → en → kk → ''.
 *     - Returns '' for null/undefined.
 */

export type SupportedLanguage = 'ru' | 'en' | 'kk';

export interface LocalizedObject {
  ru: string;
  en?: string;
  kk?: string;
}

/** Field that may be a plain string (legacy) or a per-language object. */
export type Localized = string | LocalizedObject;

const FALLBACK_ORDER: SupportedLanguage[] = ['ru', 'en', 'kk'];

/** Type guard — `true` when the value is a localized object, not a plain
 *  string. Useful in admin forms that need to switch UI based on shape. */
export const isLocalizedObject = (value: unknown): value is LocalizedObject => {
  return (
    typeof value === 'object'
    && value !== null
    && !Array.isArray(value)
    && typeof (value as LocalizedObject).ru === 'string'
  );
};

/** Resolve a Localized value to a single string for the given language.
 *  Falls back to RU → EN → KK → '' if the requested language is missing. */
export const pickLocalized = (value: Localized | null | undefined, lang: SupportedLanguage): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (value[lang]) return value[lang]!;
  for (const fallback of FALLBACK_ORDER) {
    if (value[fallback]) return value[fallback]!;
  }
  return '';
};

/** Empty localized object — handy seed for new fields in the admin form. */
export const emptyLocalized = (): LocalizedObject => ({ ru: '', en: '', kk: '' });

/** True when no language has any non-empty content. */
export const isLocalizedEmpty = (value: Localized | null | undefined): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return !(value.ru?.trim() || value.en?.trim() || value.kk?.trim());
};

/** Normalise a Localized for transport. Drops empty languages, returns the
 *  RU string when only RU is present (so the wire payload stays compact for
 *  single-language broadcasts). Returns null when fully empty. */
export const compactLocalized = (value: Localized | null | undefined): Localized | null => {
  if (value == null) return null;
  if (typeof value === 'string') {
    return value.trim() === '' ? null : value;
  }
  const ru = value.ru?.trim() || '';
  const en = value.en?.trim() || '';
  const kk = value.kk?.trim() || '';
  if (!ru && !en && !kk) return null;
  if (ru && !en && !kk) return ru;
  const out: LocalizedObject = { ru };
  if (en) out.en = en;
  if (kk) out.kk = kk;
  return out;
};

/* ------------------------------------------------------------------ */
/* Display context — where in the app a notification is allowed to    */
/* surface. Filtered client-side in NotificationManager.              */
/* ------------------------------------------------------------------ */

/** Top-level page groups available in the admin "where to show" picker.
 *  Maps to one or more route prefixes — see PAGE_PATHS below. */
export type DisplayPage =
  | 'tools'      // ANY tool — see ToolPage when tools.* is set
  | 'account'    // /account*
  | 'pricing'    // /pricing
  | 'landing'    // /
  | 'login';     // /login

/** Sub-tool selection. Used when the admin wants to surface only on
 *  specific tools instead of "any tool". When `tools` is missing in
 *  DisplayContext, the entire tools group is allowed. */
export type ToolPage = 'chat' | 'image' | 'imageedit' | 'video' | 'upscale' | 'wardrobe';

export type DisplayContext =
  | { mode: 'everywhere' }
  | { mode: 'app-only' }       // any page except landing/login
  | {
      mode: 'specific';
      pages: DisplayPage[];
      /** When 'tools' is in `pages`, optionally narrow it down to specific
       *  tools. Empty/missing → all tools allowed. */
      tools?: ToolPage[];
    };

const TOOL_PATHS: Record<ToolPage, string> = {
  chat: '/chat',
  image: '/image',
  imageedit: '/imageedit',
  video: '/video',
  upscale: '/upscale',
  wardrobe: '/wardrobe',
};

const PAGE_PATHS: Record<Exclude<DisplayPage, 'tools'>, string[]> = {
  account: ['/account'],
  pricing: ['/pricing'],
  landing: ['/'],
  login: ['/login'],
};

const PUBLIC_PATHS: string[] = ['/', '/login'];

const matchesNonToolPage = (currentPath: string, page: Exclude<DisplayPage, 'tools'>): boolean => {
  if (page === 'landing') {
    // Landing is exact-match only; otherwise it would swallow every other path.
    return currentPath === '/';
  }
  const prefixes = PAGE_PATHS[page];
  return prefixes.some((p) => currentPath === p || currentPath.startsWith(p + '/'));
};

const matchesTool = (currentPath: string, tool: ToolPage): boolean => {
  const prefix = TOOL_PATHS[tool];
  return currentPath === prefix || currentPath.startsWith(prefix + '/');
};

const matchesAnyTool = (currentPath: string): boolean =>
  (Object.keys(TOOL_PATHS) as ToolPage[]).some((t) => matchesTool(currentPath, t));

/** True when a notification with the given displayContext is allowed to
 *  surface on the given path. Missing context → 'everywhere'. */
export const matchesDisplayContext = (
  context: DisplayContext | null | undefined,
  currentPath: string,
): boolean => {
  if (!context || context.mode === 'everywhere') return true;
  if (context.mode === 'app-only') {
    return !PUBLIC_PATHS.includes(currentPath);
  }
  if (context.mode === 'specific') {
    for (const page of context.pages) {
      if (page === 'tools') {
        const subTools = context.tools && context.tools.length > 0 ? context.tools : null;
        if (subTools) {
          if (subTools.some((t) => matchesTool(currentPath, t))) return true;
        } else if (matchesAnyTool(currentPath)) {
          return true;
        }
      } else if (matchesNonToolPage(currentPath, page)) {
        return true;
      }
    }
    return false;
  }
  return true;
};
